import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { getErrorMessage } from "services/api";
import { getItemGroups } from "features/Products/api";
import {
  transformItemsGroup,
  findGroupsAllChildrenIds,
} from "features/Products/helpers";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Radio } from "components/Radio";
import { Checkbox } from "components/Checkbox";
import { Accordion } from "components/Accordion";
import { TimePicker } from "components/TimePicker";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";
import { LoadingView, ErrorView } from "components/Feedback";
import {
  useModal,
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/Modal";

import { createDiscount, updateDiscount } from "./api";
import styles from "./styles.module.scss";

const LABEL = 150;
const INPUT_W = 220;

/** Модалка создания/редактирования скидки */
export const DiscountModal = ({ discount }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const isEdit = Boolean(discount?.discountid);

  const [name, setName] = useState(discount?.name ?? "");
  const [importcode, setImportcode] = useState(discount?.importcode ?? "");
  const [card, setCard] = useState(discount?.card ?? "");
  const [position, setPosition] = useState(
    discount?.position != null ? String(discount.position) : ""
  );
  const [value, setValue] = useState(
    discount?.value != null ? String(discount.value) : ""
  );
  // mode: 1 — процентная, 0 — рублёвая
  const [mode, setMode] = useState(
    discount?.mode != null ? Number(discount.mode) : 1
  );
  const [starttime, setStarttime] = useState(discount?.starttime ?? "");
  const [endtime, setEndtime] = useState(discount?.endtime ?? "");
  const [avaliable, setAvaliable] = useState(
    discount ? Number(discount.avaliable) === 1 : true
  );

  const [selectedGroups, setSelectedGroups] = useState(
    () => new Set((discount?.itemgroupids ?? []).map(Number))
  );
  const [expanded, setExpanded] = useState(new Set());

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  /* ---- дерево групп товаров ---- */

  const groupsQuery = useQuery(["item-groups"], () => getItemGroups());
  const groups = useMemo(
    () => groupsQuery.data?.items ?? [],
    [groupsQuery.data]
  );
  const tree = useMemo(() => transformItemsGroup(groups), [groups]);

  // Выбор группы тянет всех потомков; снятие — снимает всех потомков.
  // Ручное переключение потомков на родителя не влияет.
  const toggleGroup = (groupId) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      const id = Number(groupId);
      const childrenIds = findGroupsAllChildrenIds(groups, [groupId]).map(
        Number
      );
      if (next.has(id)) {
        next.delete(id);
        childrenIds.forEach((c) => next.delete(c));
      } else {
        next.add(id);
        childrenIds.forEach((c) => next.add(c));
      }
      return next;
    });
  };

  const toggleAccordion = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderGroupTree = (nodes) =>
    nodes.map((node) => {
      const checkbox = (
        <div className={styles.groupRow} key={node.itemgroupid}>
          <Checkbox
            checked={selectedGroups.has(Number(node.itemgroupid))}
            onChange={() => toggleGroup(node.itemgroupid)}
            label={node.name}
          />
        </div>
      );

      if (node.children?.length) {
        return (
          <Accordion
            key={node.itemgroupid}
            withIcon
            arrowZone
            collapsed={!expanded.has(node.itemgroupid)}
            handleCollapse={() => toggleAccordion(node.itemgroupid)}
            parent={checkbox}
          >
            <div className={styles.treeChildren}>
              {renderGroupTree(node.children)}
            </div>
          </Accordion>
        );
      }
      return checkbox;
    });

  /* ---- сохранение ---- */

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Укажите наименование");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        importcode: importcode.trim() || null,
        card: card.trim() || null,
        position: position === "" ? null : Number(position),
        value: value === "" ? null : Number(value),
        mode,
        starttime: starttime || null,
        endtime: endtime || null,
        avaliable: avaliable ? 1 : 0,
        itemgroupids: [...selectedGroups],
      };
      if (isEdit) {
        await updateDiscount({ ...payload, discountid: discount.discountid });
      } else {
        await createDiscount(payload);
      }
      queryClient.invalidateQueries(["discounts"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell className={styles.modal}>
      <ModalHeader
        title={isEdit ? `Скидка "${discount.name}"` : "Новая скидка"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.columns}>
          <div className={styles.fieldsColumn}>
            {isEdit && (
              <>
                <Input
                  label="Код"
                  labelWidth={LABEL}
                  value={discount.discountid}
                  disabled
                  inputWidth={100}
                />
                <VerticalSpace size={12} />
              </>
            )}
            <Input
              label="Наименование"
              labelWidth={LABEL}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              inputWidth={INPUT_W}
            />
            <VerticalSpace size={12} />
            <Input
              label="Внешний код"
              labelWidth={LABEL}
              value={importcode}
              onChange={(ev) => setImportcode(ev.target.value)}
              inputWidth={INPUT_W}
            />
            <VerticalSpace size={12} />
            <Input
              label="Номер карты"
              labelWidth={LABEL}
              value={card}
              onChange={(ev) => setCard(ev.target.value)}
              inputWidth={INPUT_W}
            />
            <VerticalSpace size={12} />
            <Input
              label="Позиция в списке"
              labelWidth={LABEL}
              type="number"
              value={position}
              onChange={(ev) => setPosition(ev.target.value)}
              inputWidth={100}
            />
            <VerticalSpace size={12} />
            <Input
              label="Величина скидки"
              labelWidth={LABEL}
              type="number"
              value={value}
              onChange={(ev) => setValue(ev.target.value)}
              inputWidth={100}
            />
            <VerticalSpace size={16} />
            <div className={styles.modeRow}>
              <MediumText size={13} style={{ width: LABEL, flexShrink: 0 }}>
                Вид скидки:
              </MediumText>
              <Radio
                id="discount-mode-percent"
                name="discount-mode"
                label="Процентная"
                checked={mode === 1}
                onChange={() => setMode(1)}
              />
              <Radio
                id="discount-mode-currency"
                name="discount-mode"
                label="Рублёвая"
                checked={mode === 0}
                onChange={() => setMode(0)}
              />
            </div>
            <VerticalSpace size={16} />
            <div className={styles.timeRow}>
              <MediumText size={13} style={{ width: LABEL, flexShrink: 0 }}>
                Время действия:
              </MediumText>
              <TimePicker
                label="с"
                value={starttime || null}
                onChange={(val) => setStarttime(val || "")}
              />
              <TimePicker
                label="по"
                value={endtime || null}
                onChange={(val) => setEndtime(val || "")}
              />
            </div>
            <VerticalSpace size={16} />
            <Checkbox
              label="Скидка действует"
              checked={avaliable}
              onChange={setAvaliable}
            />
          </div>

          <div className={styles.groupsColumn}>
            <MediumText size={14}>Группы товаров</MediumText>
            <VerticalSpace size={8} />
            <div className={styles.groupTree}>
              {groupsQuery.isLoading ? (
                <LoadingView />
              ) : groupsQuery.isError ? (
                <ErrorView
                  message={getErrorMessage(groupsQuery.error)}
                  onRetry={groupsQuery.refetch}
                />
              ) : (
                renderGroupTree(tree)
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {!!error && (
          <RegularText size={13} color="danger" className={styles.footerError}>
            {error}
          </RegularText>
        )}
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
        <Button variant="tertiary" onClick={closeModal}>
          Отмена
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
